import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { setCustomer } from "../../../ReduxToolkit/Reducers/CartReducer";
import { addNewCustomer } from "../../../ReduxToolkit/Reducers/AppReducer";
import { toast } from "react-toastify";
import http from "../../../services/http";
import { getAPIURL, getVendorID } from "../../../services/helper";

const CustomerModal = ({ isOpen, toggle, onProceed }) => {
  const dispatch = useDispatch();
  const { vendorCustomers } = useSelector((state) => state.app);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchInputRef = React.useRef(null);

  // Clear selection when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedCustomer(null);
      setSearchQuery("");
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      }, 100); // Delay to ensure modal is rendered
    }
  }, [isOpen]);

  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    address: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Auto-select if exact customer_unique_id match
  React.useEffect(() => {
    if (searchQuery && vendorCustomers) {
      const exactMatch = vendorCustomers.find(
        (customer) =>
          customer.customer_unique_id?.toLowerCase() ===
          searchQuery.toLowerCase()
      );
      if (exactMatch && selectedCustomer?.id !== exactMatch.id) {
        handleCustomerSelect(exactMatch);
      }
    }
  }, [searchQuery, vendorCustomers]);

  const filteredCustomers =
    vendorCustomers?.filter((customer) => {
      const fullName = `${customer.full_name || ""}`.toLowerCase();
      const query = searchQuery.toLowerCase();
      return (
        fullName.includes(query) ||
        customer.email?.toLowerCase().includes(query) ||
        customer.phone_number?.includes(query) ||
        customer.customer_unique_id?.toLowerCase().includes(query) ||
        customer.id?.toString().includes(searchQuery)
      );
    }) || [];

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    dispatch(setCustomer(customer.id));
    // Auto proceed to payment when customer selected
    setTimeout(() => {
      handleProceed();
    }, 300);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.full_name.trim()) {
      toast.error("First name is required");
      return;
    }

    const payload = { ...newCustomer, vendor_id: getVendorID() };

    setIsLoading(true);
    try {
      const response = await http.post(`${getAPIURL()}/customers`, payload);
      if (response.data.success) {
        const customer = response.data.data;
        dispatch(addNewCustomer(customer));
        dispatch(setCustomer(customer.id));
        setSelectedCustomer(customer);
        setShowNewCustomerForm(false);
        setNewCustomer({
          full_name: "",
          email: "",
          phone_number: "",
          address: "",
        });
        toast.success("Customer created successfully");
      }
    } catch (error) {
      toast.error("Failed to create customer");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = () => {
    if (!selectedCustomer) {
      dispatch(setCustomer(0));
    }
    onProceed();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Select Customer</ModalHeader>
      <ModalBody>
        {!showNewCustomerForm ? (
          <>
            <div className="mb-3 position-relative">
              <Input
                innerRef={searchInputRef}
                type="text"
                placeholder="Search by name, ID, phone, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              {searchQuery && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary position-absolute"
                  style={{
                    right: "5px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: "2px 6px",
                  }}
                  onClick={() => setSearchQuery("")}
                >
                  ×
                </button>
              )}
            </div>

            <div
              style={{
                maxHeight: selectedCustomer ? "100px" : "300px",
                overflowY: "auto",
              }}
            >
              {filteredCustomers.length === 0 && searchQuery ? (
                <div className="text-center p-3 text-muted">
                  No customers found matching "{searchQuery}"
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className={`p-2 border rounded mb-2 cursor-pointer ${
                      selectedCustomer?.id === customer.id
                        ? "bg-primary text-white"
                        : "bg-teal"
                    }`}
                    style={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedCustomer?.id === customer.id ? "" : "#20b2aa",
                      color:
                        selectedCustomer?.id === customer.id ? "" : "white",
                    }}
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    <div>
                      <strong>{customer.first_name}</strong>
                    </div>
                    <div className="small">
                      ID: {customer.customer_unique_id} |{" "}
                      {customer.phone_number}
                      {customer.customer_points > 0 && (
                        <span className="badge bg-warning ms-2">
                          {customer.customer_points} pts
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {!selectedCustomer && (
              <div className="mt-3 text-center">
                <Button
                  color="outline-primary"
                  onClick={() => setShowNewCustomerForm(true)}
                >
                  + Create New Customer
                </Button>
              </div>
            )}
          </>
        ) : (
          <Form>
            <FormGroup>
              <Label>Full Name *</Label>
              <Input
                type="text"
                value={newCustomer.full_name}
                onChange={(e) =>
                  setNewCustomer({ ...newCustomer, full_name: e.target.value })
                }
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) =>
                  setNewCustomer({ ...newCustomer, email: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>Phone</Label>
              <Input
                type="text"
                value={newCustomer.phone_number}
                onChange={(e) =>
                  setNewCustomer({
                    ...newCustomer,
                    phone_number: e.target.value,
                  })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>Address</Label>
              <Input
                type="textarea"
                value={newCustomer.address}
                onChange={(e) =>
                  setNewCustomer({ ...newCustomer, address: e.target.value })
                }
              />
            </FormGroup>
            <div className="d-flex gap-2">
              <Button
                color="success"
                onClick={handleCreateCustomer}
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create Customer"}
              </Button>
              <Button
                color="outline-secondary"
                onClick={() => setShowNewCustomerForm(false)}
              >
                ← Back to Search
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        {selectedCustomer && (
          <Button
            color="outline-danger"
            size="sm"
            onClick={() => {
              setSelectedCustomer(null);
              dispatch(setCustomer(0));
            }}
          >
            × Clear
          </Button>
        )}
        <Button color="success" onClick={handleProceed} className="ms-auto">
          {selectedCustomer
            ? `Pay with ${selectedCustomer.full_name}`
            : "Pay without Customer"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CustomerModal;
